export const JOB_BOARD_CHANNEL_KEYS = ['wttj', 'hellowork', 'apec', 'jobteaser', 'linkedin', 'indeed', 'francetravail'] as const
export type TJobBoardChannelKey = (typeof JOB_BOARD_CHANNEL_KEYS)[number]

export type TJobBoardChannel = {
  key: TJobBoardChannelKey
  label: string
  /** The board reads the public feed of the offers ticked for it: the firm turns it on in the career site settings */
  hasFeed: boolean
  /** The board sends the applications it receives straight to BestConsultant (no redirection to the career site) */
  pushesApplications: boolean
  /**
   * The automatic posting can be set up by the firms (partnership signed, integration tested). False: its feed and
   * settings are hidden and refused; the tracked application link and the site of the firm's list still work.
   */
  isReady: boolean
}

/**
 * Job boards an offer can be distributed to. Every board gets a tracked application link (`?source=<key>`),
 * so that a manual posting (LinkedIn...) also tells where the candidate comes from.
 */
export const JOB_BOARD_CHANNELS: TJobBoardChannel[] = [
  { key: 'wttj', label: 'Welcome to the Jungle', hasFeed: true, pushesApplications: false, isReady: false },
  { key: 'hellowork', label: 'HelloWork', hasFeed: true, pushesApplications: true, isReady: false },
  { key: 'jobteaser', label: 'JobTeaser', hasFeed: true, pushesApplications: false, isReady: false },
  { key: 'apec', label: 'APEC', hasFeed: false, pushesApplications: false, isReady: false },
  { key: 'linkedin', label: 'LinkedIn', hasFeed: false, pushesApplications: false, isReady: false },
  { key: 'indeed', label: 'Indeed', hasFeed: false, pushesApplications: false, isReady: false },
  { key: 'francetravail', label: 'France Travail', hasFeed: false, pushesApplications: false, isReady: false },
]

/** Query parameter of the career site URLs that carries the channel */
export const CAREER_CHANNEL_QUERY = 'source'

const CHANNEL_ALIASES: Record<string, TJobBoardChannelKey> = {
  'welcometothejungle': 'wttj',
  'welcome-to-the-jungle': 'wttj',
  'welcome_to_the_jungle': 'wttj',
  'hellowork.com': 'hellowork',
  'linkedin.com': 'linkedin',
  'indeed.com': 'indeed',
  'indeed.fr': 'indeed',
  'apec.fr': 'apec',
  'france-travail': 'francetravail',
  'france_travail': 'francetravail',
  'pole-emploi': 'francetravail',
  'poleemploi': 'francetravail',
}

export const isJobBoardChannel = (value: unknown): value is TJobBoardChannelKey =>
  typeof value === 'string' && (JOB_BOARD_CHANNEL_KEYS as readonly string[]).includes(value)

/** Channel key of a `?source=` / `utm_source` value, null when it is not a known board */
export const normalizeJobBoardChannel = (value: unknown): TJobBoardChannelKey | null => {
  if (typeof value !== 'string') return null
  const cleaned = value.trim().toLowerCase()
  if (isJobBoardChannel(cleaned)) return cleaned
  return CHANNEL_ALIASES[cleaned] ?? null
}

export const getJobBoardChannel = (key: unknown): TJobBoardChannel | undefined =>
  JOB_BOARD_CHANNELS.find(channel => channel.key === key)

/** A board whose feed the firms can turn on today */
export const isJobBoardFeedReady = (channel: Pick<TJobBoardChannel, 'hasFeed' | 'isReady'> | null | undefined) =>
  Boolean(channel?.hasFeed && channel.isReady)

/** At least one board can be set up: the job board fields of the offers are useful */
export const hasReadyJobBoard = () => JOB_BOARD_CHANNELS.some(channel => channel.isReady)

export const getCareerOfferPath = (firmId: string, offerId: string) => `/recruitment/career/${firmId}/${offerId}`

/** Public URL of an offer that tags the applications with the channel */
export const getCareerOfferChannelUrl = (baseUrl: string, firmId: string, offerId: string, channel: TJobBoardChannelKey) =>
  `${baseUrl.replace(/\/+$/, '')}${getCareerOfferPath(firmId, offerId)}?${CAREER_CHANNEL_QUERY}=${channel}`

// Job board fields of an offer, mapped to each board's own referential when it is sent
export const CONTRACT_KINDS = ['permanent', 'fixedTerm', 'internship', 'apprenticeship', 'freelance', 'temporary'] as const
export type TContractKind = (typeof CONTRACT_KINDS)[number]

export const WORKING_TIMES = ['fullTime', 'partTime'] as const
export type TWorkingTime = (typeof WORKING_TIMES)[number]

export const REMOTE_POLICIES = ['none', 'partial', 'full'] as const
export type TRemotePolicy = (typeof REMOTE_POLICIES)[number]

export const SALARY_PERIODS = ['year', 'month', 'day', 'hour'] as const
export type TSalaryPeriod = (typeof SALARY_PERIODS)[number]
